import { axiosJSONInstance, OrderStatusEnum } from '@/config';
import { ISearch } from '@/hooks/useSearchStore';
import {
  ActivityLogResponse,
  AsyncPreviewResponse,
  AsyncRequestOrder,
  Order,
  OrderListResponse,
  OrderPriceResponse,
  OrderProofOfDelivery,
  OrderResolveAction,
  OrderResolveResponse,
  OrderRtoRequest,
  OrderRtoResponse,
  OrderSingleResponse,
  OrderSingleRtoResponse,
  OrdersResponse,
  OrderStatusCountResponse,
  OrderTransaction,
  Product,
  ResponseListType,
  ResponseType,
  StatusAnnotation,
} from '@/types'; // Ensure this path is correct based on your folder structure
import { createPaginationSearchParams } from '@/utils';

// Fetch all orders
export const fetchOrders = async (): Promise<OrdersResponse> => {
  try {
    const response = await axiosJSONInstance.get(`/merchants/orders`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch orders:', error);
    throw error; // Rethrow the error to be caught in the useQuery onError
  }
};

// Fetch an order by UID
export const fetchOrderByUID = async (uid: string): Promise<OrderSingleResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/orders/${uid}`);
  return response.data;
};

// Fetch Order Proof Of Delivery
export const fetchOrderProofOfDelivery = async (
  uid: string,
): Promise<ResponseType<OrderProofOfDelivery>> => {
  const response = await axiosJSONInstance.get(`/merchants/orders/${uid}/carrier-proof`);
  return response.data;
};

// Fetch an order by UID
export const fetchOrderCount = async (): Promise<ResponseType<OrderStatusCountResponse>> => {
  const response = await axiosJSONInstance.get(`/merchants/orders/badge-counts`);
  return response.data;
};

// Fetch an order by UID
export const fetchTransactionByOrderID = async (
  uid: string,
): Promise<ResponseListType<OrderTransaction>> => {
  const response = await axiosJSONInstance.get(`/merchants/orders/${uid}/transactions`, {
    params: {
      perpage: 200,
      page: 1,
    },
  });
  return response.data;
};

// Create a new order
export const createOrder = async (order: Order): Promise<OrdersResponse> => {
  const response = await axiosJSONInstance.post('/merchants/orders/request', order);
  return response.data;
};

export const asyncCreateOrders = async (order: AsyncRequestOrder): Promise<OrdersResponse> => {
  const response = await axiosJSONInstance.post('/merchants/orders/bulk-request', order);
  return response.data;
};
// Preview order price
export const previewOrderPrice = async (order: Order): Promise<OrderPriceResponse> => {
  const response = await axiosJSONInstance.post('/merchants/orders/preview', order);
  return response.data;
};
//Async Preview order price
export const asyncPreviewOrderPrice = async (
  order: AsyncRequestOrder,
): Promise<ResponseType<AsyncPreviewResponse>> => {
  const response = await axiosJSONInstance.post('/merchants/orders/bulk-preview', order);

  return response.data;
};

// Update an order by UID
export const updateOrder = async (
  order: Partial<Order> & { uid: string },
): Promise<OrdersResponse> => {
  const response = await axiosJSONInstance.patch(`/merchants/orders/${order.uid}`, order);
  return response.data;
};

// Delete an order by UID
export const deleteOrder = async (uid: string): Promise<void> => {
  await axiosJSONInstance.delete(`/merchants/orders/${uid}`);
};

//Bulk Delete orders
export const bulkDeleteOrders = async (
  status?: OrderStatusEnum,
  selectedIds?: string[],
): Promise<void> => {
  const queryParams: string[] = [];

  // Same 'all' sub-tab sentinel guard as bulkCancelOrders below — 'all' is not a
  // real status slug, so emitting it makes the backend match zero statuses and
  // delete nothing. Today's Orders-page caller passes a hardcoded status, but
  // this is a shared exported helper and the failure is silent.
  if (status && String(status) !== 'all') queryParams.push(`filter_statuses[]=${status}`);

  if (selectedIds?.length) {
    const idsQuery = selectedIds.map((id) => `filter_uids[]=${id}`).join('&');
    queryParams.push(idsQuery);
  }

  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
  const url = `/merchants/orders/bulk-delete${queryString}`;

  await axiosJSONInstance.post(url);
};

// Cancel an order by UID
export const cancelOrder = async (uid: string): Promise<OrdersResponse> => {
  const response = await axiosJSONInstance.post(`/merchants/orders/${uid}/cancel`);
  return response.data;
};

//Bulk Cancel orders
export const bulkCancelOrders = async (
  status?: OrderStatusEnum,
  selectedIds?: string[],
): Promise<void> => {
  const queryParams: string[] = [];

  // 'all' is the Orders-page sub-tab sentinel, not a real status slug. Emitting
  // it makes the backend match zero statuses and cancel nothing, so an id-based
  // selection silently fails ("there is no order with current filter"). Skip it.
  if (status && String(status) !== 'all') queryParams.push(`filter_statuses[]=${status}`);

  if (selectedIds?.length) {
    const idsQuery = selectedIds.map((id) => `filter_uids[]=${id}`).join('&');
    queryParams.push(idsQuery);
  }

  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';
  const url = `/merchants/orders/bulk-cancel${queryString}`;

  await axiosJSONInstance.post(url);
};

// Add a new product to an order
export const addProductToOrder = async (
  orderUID: string,
  product: Product,
): Promise<OrdersResponse> => {
  const response = await axiosJSONInstance.post(`/merchants/orders/${orderUID}/products/`, product);
  return response.data;
};

// Delete a new product to an order
export const deleteProductFromOrder = async (
  orderUID: string,
  productUID: string,
): Promise<OrdersResponse> => {
  const response = await axiosJSONInstance.delete(
    `/merchants/orders/${orderUID}/products/${productUID}`,
  );
  return response.data;
};

// Update a product by UID for a specific order
export const updateProductByUID = async (
  orderUID: string,
  productUID: string,
  updatedProduct: Partial<Product>,
): Promise<Product> => {
  const response = await axiosJSONInstance.patch(
    `/merchants/orders/${orderUID}/products/${productUID}`,
    updatedProduct,
  );
  return response.data;
};

// Fetch Single label Pickup by UID
export const fetchSingleLabelPickupByUID = async (uid: string): Promise<File> => {
  const response = await axiosJSONInstance.get(`/merchants/orders/${uid}/pick-up-labels`, {
    responseType: 'blob',
    timeout: 600000, //10 minutes
  });
  return response.data;
};

// Fetch Bulk label Pickup by UIDs
export const fetchBulkLabelPickupByUIDs = async (data: string): Promise<File> => {
  const response = await axiosJSONInstance.get(`merchants/orders/pick-up-labels?${data}`, {
    responseType: 'blob',
    timeout: 600000, //10 minutes
  });
  return response.data;
};

// order list status

// Fetch List Order ReadyToShip
export const fetchListOrderReady = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/ready-to-ship?${params}`);

  return response.data;
};

// fetch List Order All
export const fetchListOrders = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');

  const response = await axiosJSONInstance.get(`/merchants/orders?${params}`);
  return response.data;
};

// Fetch List Order Inprogress
export const fetchListInprogress = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/in-progress?${params}`);
  return response.data;
};

// Fetch List Order OnHold
export const fetchListOnHold = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/on-hold?${params}`);
  return response.data;
};

// Fetch List Order Finished
export const fetchListFinished = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/finished?${params}`);
  return response.data;
};

// Fetch List By Status Slug
export const fetchListByStatusSlug = async (
  slug: string,
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/status/${slug}?${params}`);
  return response.data;
};

// Fetch Action Required Order List
export const fetchActionRequiredOrderList = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/action-required?${params}`);
  return response.data;
};

//Resolve order action
export const resolveActionOrder = async (
  orderId: string,
  resolveAction: OrderResolveAction,
): Promise<OrderResolveResponse> => {
  const response = await axiosJSONInstance.post(
    `/merchants/orders/${orderId}/resolve`,
    resolveAction,
  );
  return response.data;
};

//RTO request order
export const rtoRequestOrder = async (
  orderId: string,
  rtoRequest: OrderRtoRequest,
): Promise<OrderRtoResponse> => {
  const response = await axiosJSONInstance.post(
    `/merchants/orders/${orderId}/request-rto`,
    rtoRequest,
  );
  return response.data;
};

// RTO List
export const fetchOrderRTO = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/rto?${params}`);
  return response.data;
};

// RTO Request
export const fetchOrderRtoRequest = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/rto-request?${params}`);
  return response.data;
};

// RTO Order By Id
export const fetchOrderRTOById = async (uid: string): Promise<OrderSingleRtoResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/rto/${uid}`);
  return response.data;
};

// Fetch an order collecting
export const fetchOrderCollecting = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`merchants/orders/collecting?${params}`);
  return response.data;
};

// Fetch an order en-route
export const fetchOrderEnRoute = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`merchants/orders/en-route?${params}`);
  return response.data;
};

// Fetch Bulk orders by UIDs
export const fetchBulkOrderByQuery = async (queryString: string): Promise<File> => {
  const response = await axiosJSONInstance.get(`merchants/orders/export?${queryString}`, {
    responseType: 'blob',
    timeout: 600000, //10 minutes
  });
  return response.data;
};

export const fetchOrderActivityLogByOrderUID = async (
  uid: string,
  page = 1,
  perpage = 20,
): Promise<ActivityLogResponse> => {
  const response = await axiosJSONInstance.get(`merchants/orders/${uid}/activity-logs`, {
    params: {
      perpage,
      page,
    },
  });

  return response.data;
};

// Fetch List Order RTO returning-origin
export const fetchListReturningOrigin = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/returning-origin?${params}`);
  return response.data;
};

// Fetch List Order RTO returning-warehouse
export const fetchListReturningWarehouse = async (
  page: number,
  pageSize?: number,
  search?: ISearch,
  filterParams?: URLSearchParams,
): Promise<OrderListResponse> => {
  const searchParams = createPaginationSearchParams(page, pageSize, search);
  const params = searchParams.toString() + (filterParams ? '&' + filterParams?.toString() : '');
  const response = await axiosJSONInstance.get(`/merchants/orders/returned-warehouse?${params}`);
  return response.data;
};

// Duplicate an order by UID
export const duplicateOrder = async (orderId: string): Promise<OrderSingleResponse> => {
  const response = await axiosJSONInstance.post(`/merchants/orders/${orderId}/duplicate`);
  return response.data;
};

// Fetch Order Status Annotations by UID
export const fetchStatusAnnotationsByOrderUID = async (
  orderId: string,
): Promise<ResponseListType<StatusAnnotation>> => {
  const response = await axiosJSONInstance.get(`/merchants/orders/${orderId}/status-annotations`);
  return response.data;
};

// Fetch active ticket for an order
export const fetchOrderActiveTicket = async (
  orderUid: string,
): Promise<{ status: string; object: { id: number; subject: string; status: string; created_at: string } | null }> => {
  const response = await axiosJSONInstance.get(`/merchants/orders/${orderUid}/active-ticket`);
  return response.data;
};

// Fetch Order Call Logs
export interface CallLogItem {
  id: number;
  type: string;
  status: string;
  duration?: number;
  note?: string;
  created_at: string;
  agent_name?: string;
}

export interface CallLogResponse {
  status: string;
  code: number;
  message: string;
  object: {
    items: CallLogItem[];
    total: number;
  };
}

export const fetchOrderCallLogs = async (uid: string): Promise<CallLogResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/orders/${uid}/call-logs`);
  return response.data;
};

// Meilisearch-powered order search
export const fetchSearchOrders = async (
  page: number,
  pageSize?: number,
  query?: string,
  searchOnAttributes?: string[],
  statusGroup?: string,
  filterParams?: URLSearchParams,
  filters?: { key: string; value: string }[],
): Promise<OrderListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: (pageSize || 25).toString(),
  });

  if (query) {
    params.append('query', query);
  }

  if (searchOnAttributes && searchOnAttributes.length > 0) {
    searchOnAttributes.forEach((attr) => {
      params.append('search_on_attributes[]', attr);
    });
  }

  if (statusGroup) {
    params.append('status_group', statusGroup);
  }

  // Append date filters if present
  if (filterParams) {
    const fromDate = filterParams.get('filter_created_at_from');
    const toDate = filterParams.get('filter_created_at_to');
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
  }

  // Build Meilisearch filters[] array — combine explicit filters + type/carrier from filterParams
  const allFilters: { key: string; value: string | string[] }[] = [...(filters ?? [])];

  // Convert filter_types[] → filters[]{key:'type', value:[...]}
  if (filterParams) {
    const types = filterParams.getAll('filter_types[]');
    if (types.length > 0) {
      allFilters.push({ key: 'type', value: types.length === 1 ? types[0] : types });
    }
    const carriers = filterParams.getAll('filter_carriers[]');
    if (carriers.length > 0) {
      allFilters.push({ key: 'carrier.slug', value: carriers.length === 1 ? carriers[0] : carriers });
    }
  }

  if (allFilters.length > 0) {
    allFilters.forEach((f, i) => {
      params.append(`filters[${i}][key]`, f.key);
      if (Array.isArray(f.value)) {
        f.value.forEach((v) => params.append(`filters[${i}][value][]`, v));
      } else {
        params.append(`filters[${i}][value]`, f.value);
      }
    });
  }

  const response = await axiosJSONInstance.get(`/merchants/orders/search?${params.toString()}`);
  return response.data;
};
